// Files in a document (images, videos and attachments). The syntax is in
// file-syntax.ts. Two nodes:
//
// - `trameriaFile` (block atom): only the `!` form that takes a whole line - the
//   images and videos of the body;
// - `trameriaFileInline` (inline atom): everything else - a chip with the name.
//   It keeps `image` (the `!`) so the syntax round-trips, but is always drawn
//   as a chip: a tall image in the middle of a line would stretch the line.
//
// The width (`?w=<px>`) belongs to block images only; a chip drops it. How a
// width is read is decided by `normalizeFileWidth` alone.
//
// This package knows neither the file content nor its type. A node holds the
// id, the name and whether to draw an image; the screen resolves the URL.
//
// marked tries extension inline tokenizers before its own (escape, tag, link),
// so `![name](trameria://file/<uuid>)` in the middle of a paragraph is taken
// here before the built-in image tokenizer would drop the id (the file would
// then have no reference and be deleted). Code is never seen: fences are taken
// whole by the block tokenizer, and a code span is consumed from its opening
// backtick. An external `![alt](https://...)` still keeps only its alt text.
import { Node } from "@tiptap/core";
import { blockIdDomAttrs } from "./block-id";
import {
  FILE_NAME_SRC,
  FILE_QUERY_SRC,
  FILE_UUID_SRC,
  fileNodeToRaw,
  isFileId,
  normalizeFileWidth,
  parseFileQuery,
  type TrameriaFileAttrs,
  unescapeFileName,
} from "./file-syntax";

/**
 * A whole line from the start of the block. The `!` is required: the form
 * without it stays a paragraph, where `trameriaFileInline` takes it (several
 * per line). The query matches anything (`FILE_QUERY_SRC`).
 */
const TOKEN_RE = new RegExp(
  `^!\\[(${FILE_NAME_SRC})\\]\\(trameria://file/(${FILE_UUID_SRC})(${FILE_QUERY_SRC})\\)[ \\t]*(?:\\n|$)`,
);

/**
 * Where a block file may start (line-anchored). It spans to the end of the line
 * like `TOKEN_RE`: with an open end it would match candidates that `TOKEN_RE`
 * rejects, and marked would cut the block there and change the text.
 */
const START_RE = new RegExp(
  `^!\\[${FILE_NAME_SRC}\\]\\(trameria://file/${FILE_UUID_SRC}${FILE_QUERY_SRC}\\)[ \\t]*$`,
  "m",
);

/**
 * The inline match: anywhere, `!` optional. A width is accepted and dropped
 * (refusing it would lose the id); the query is captured to keep `kind=video`.
 */
const INLINE_TOKEN_RE = new RegExp(
  `^(!?)\\[(${FILE_NAME_SRC})\\]\\(trameria://file/(${FILE_UUID_SRC})(${FILE_QUERY_SRC})\\)`,
);

/** Where an inline file may start; without it marked's text tokenizer would eat it. */
const INLINE_SEARCH_RE = new RegExp(
  `!?\\[${FILE_NAME_SRC}\\]\\(trameria://file/${FILE_UUID_SRC}${FILE_QUERY_SRC}\\)`,
);

/** The DOM shape `parseHTML` reads (the package has no DOM types). */
const reader = (el: unknown) => (name: string) =>
  (el as { getAttribute(n: string): string | null }).getAttribute(name);

export const TrameriaFile = Node.create({
  name: "trameriaFile",
  group: "block",
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      fileId: { default: "" },
      name: { default: "" },
      image: { default: false },
      // null = no width (the default look). Must default to null: a number
      // would make `fileNodeToRaw` write `?w=` into every untouched body.
      width: { default: null },
      // Video (`?kind=video`). false writes nothing.
      video: { default: false },
    };
  },

  // Copying between pages goes through renderHTML and the schema's DOM parser,
  // so a parse rule is needed for the node to come back.
  parseHTML() {
    return [
      {
        tag: "div[data-trameria-file]",
        getAttrs: (el) => {
          const get = reader(el);
          const fileId = (get("data-trameria-file") ?? "").toLowerCase();
          if (!isFileId(fileId)) return false;
          const image = get("data-file-image") === "true";
          return {
            fileId,
            name: get("data-file-name") ?? "",
            image,
            width: image ? normalizeFileWidth(get("data-file-width")) : null,
            video: image && get("data-file-video") === "true",
          };
        },
      },
    ];
  },

  // Own attributes only, so the block id is written explicitly.
  renderHTML({ node }) {
    const a = node.attrs as TrameriaFileAttrs;
    // The width only when set: writing the default would make a copy read back
    // as "set" and grow `?w=` in the pasted body.
    const width = a.image ? normalizeFileWidth(a.width) : null;
    return [
      "div",
      {
        "data-trameria-file": a.fileId,
        "data-file-name": a.name,
        "data-file-image": a.image ? "true" : "false",
        ...(width === null ? {} : { "data-file-width": String(width) }),
        ...(a.image && a.video ? { "data-file-video": "true" } : {}),
        ...blockIdDomAttrs(node),
      },
    ];
  },

  markdownTokenName: "trameriaFile",
  markdownTokenizer: {
    name: "trameriaFile",
    level: "block",
    start(src: string) {
      const match = src.match(START_RE);
      return match ? (match.index ?? -1) : -1;
    },
    tokenize(src: string) {
      const match = TOKEN_RE.exec(src);
      if (!match) return undefined;
      return {
        type: "trameriaFile",
        raw: match[0],
        name: match[1],
        fileId: match[2],
        // Passed as written; read once in parseMarkdown.
        query: match[3] ?? "",
      };
    },
  },
  parseMarkdown(token, h) {
    const query = parseFileQuery(String(token.query ?? ""));
    return h.createNode(
      "trameriaFile",
      {
        fileId: String(token.fileId ?? "").toLowerCase(),
        name: unescapeFileName(String(token.name ?? "")),
        // Only the `!` form gets here (TOKEN_RE).
        image: true,
        video: query.video,
        width: query.width,
      },
      [],
    );
  },
  renderMarkdown(node) {
    return fileNodeToRaw(node.attrs as TrameriaFileAttrs);
  },
});

/**
 * A file inside a paragraph (a chip with the name). No width; `image` and
 * `video` are kept only to write the syntax back. Not in `BLOCK_ID_TYPES`: an
 * inline node has no block id, so links and drags use the paragraph holding it.
 */
export const TrameriaFileInline = Node.create({
  name: "trameriaFileInline",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      fileId: { default: "" },
      name: { default: "" },
      image: { default: false },
      video: { default: false },
    };
  },

  // Its own key attribute, so it does not compete with the block's parse rule.
  parseHTML() {
    return [
      {
        tag: "span[data-trameria-file-inline]",
        getAttrs: (el) => {
          const get = reader(el);
          const fileId = (get("data-trameria-file-inline") ?? "").toLowerCase();
          if (!isFileId(fileId)) return false;
          return {
            fileId,
            name: get("data-file-name") ?? "",
            image: get("data-file-image") === "true",
            video:
              get("data-file-image") === "true" &&
              get("data-file-video") === "true",
          };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const a = node.attrs as TrameriaFileAttrs;
    return [
      "span",
      {
        "data-trameria-file-inline": a.fileId,
        "data-file-name": a.name,
        "data-file-image": a.image ? "true" : "false",
        ...(a.image && a.video ? { "data-file-video": "true" } : {}),
      },
      // The name as text too, so a view that does not draw chips still shows it.
      a.name,
    ];
  },

  markdownTokenName: "trameriaFileInline",
  markdownTokenizer: {
    name: "trameriaFileInline",
    level: "inline",
    start(src: string) {
      const match = src.match(INLINE_SEARCH_RE);
      return match ? (match.index ?? -1) : -1;
    },
    tokenize(src: string) {
      const match = INLINE_TOKEN_RE.exec(src);
      if (!match) return undefined;
      return {
        type: "trameriaFileInline",
        raw: match[0],
        image: match[1] === "!",
        name: match[2],
        fileId: match[3],
        query: match[4] ?? "",
      };
    },
  },
  parseMarkdown(token, h) {
    return h.createNode(
      "trameriaFileInline",
      {
        fileId: String(token.fileId ?? "").toLowerCase(),
        name: unescapeFileName(String(token.name ?? "")),
        image: Boolean(token.image),
        video:
          Boolean(token.image) &&
          parseFileQuery(String(token.query ?? "")).video,
      },
      [],
    );
  },
  renderMarkdown(node) {
    const a = node.attrs as TrameriaFileAttrs;
    // No width on chips.
    return fileNodeToRaw({ ...a, width: null });
  },
});
